import * as React from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

// Shared, height-capped, scrollable, type-ahead dropdown used everywhere a
// plain <select> used to be. Two flavors are exported:
//   - DropdownSelect: single value, menu-button trigger (e.g. "Layout: Inbox")
//   - MultiSelect: string[] value, checkbox list, chip/count trigger
//
// Both cap the open panel at max-h-64 with overflow-y-auto so a long option
// list (agents, statuses, custom-field choices) never pushes page layout
// around. Typing a letter while the menu is open always jumps/filters,
// whether or not the visible search input is shown (small lists just get
// the type-ahead-jump behavior without the extra input box).

export interface DropdownSelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface BaseProps {
  options: DropdownSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Show an inline search box. Defaults to true once options.length exceeds searchThreshold. */
  searchable?: boolean;
  searchThreshold?: number;
  align?: 'left' | 'right';
  className?: string;
  panelClassName?: string;
  label?: string;
  error?: string;
  'aria-label'?: string;
}

function useComboboxOpenState() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const typeAheadRef = React.useRef({ buffer: '', timer: 0 as ReturnType<typeof setTimeout> | 0 });

  React.useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  React.useEffect(() => setActiveIndex(0), [query]);

  return { open, setOpen, query, setQuery, activeIndex, setActiveIndex, containerRef, inputRef, typeAheadRef };
}

function filterOptions(options: DropdownSelectOption[], query: string) {
  if (!query) return options;
  const lower = query.toLowerCase();
  return options.filter((option) => (
    option.label.toLowerCase().includes(lower) || option.description?.toLowerCase().includes(lower)
  ));
}

/** Type-ahead-jump for closed/no-search-box lists: typing letters jumps to + highlights the best match. */
function useTypeAheadJump(
  open: boolean,
  hasVisibleSearch: boolean,
  options: DropdownSelectOption[],
  setActiveIndex: (index: number) => void,
  typeAheadRef: React.MutableRefObject<{ buffer: string; timer: ReturnType<typeof setTimeout> | 0 }>,
) {
  React.useEffect(() => {
    if (!open || hasVisibleSearch) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key.length !== 1 || event.metaKey || event.ctrlKey) return;
      const ref = typeAheadRef.current;
      clearTimeout(ref.timer as ReturnType<typeof setTimeout>);
      ref.buffer += event.key.toLowerCase();
      const match = options.findIndex((option) => option.label.toLowerCase().startsWith(ref.buffer));
      if (match >= 0) setActiveIndex(match);
      ref.timer = setTimeout(() => { ref.buffer = ''; }, 700);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, hasVisibleSearch, options, setActiveIndex, typeAheadRef]);
}

// ─── DropdownSelect (single value, menu-button trigger) ─────────────────────

interface DropdownSelectProps extends BaseProps {
  value: string;
  onChange: (value: string) => void;
  /** Custom trigger content, e.g. `Layout: <b>Inbox</b>`. Falls back to the selected label. */
  renderTrigger?: (selected: DropdownSelectOption | undefined) => React.ReactNode;
  triggerVariant?: 'field' | 'button';
}

export function DropdownSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  searchable,
  searchThreshold = 8,
  align = 'left',
  className,
  panelClassName,
  renderTrigger,
  triggerVariant = 'field',
  label,
  error,
}: DropdownSelectProps) {
  const { t } = useI18n();
  const { open, setOpen, query, setQuery, activeIndex, setActiveIndex, containerRef, inputRef, typeAheadRef } = useComboboxOpenState();
  const selected = options.find((option) => option.value === value);
  const filtered = filterOptions(options, query);
  const showSearch = searchable ?? options.length > searchThreshold;
  useTypeAheadJump(open, showSearch, options, setActiveIndex, typeAheadRef);

  React.useEffect(() => {
    if (open && showSearch) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, showSearch, inputRef]);

  function select(option: DropdownSelectOption) {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') { setOpen(false); return; }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(Math.min(activeIndex + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(Math.max(activeIndex - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (filtered[activeIndex]) select(filtered[activeIndex]);
    }
  }

  return (
    <div ref={containerRef} className={cn('relative inline-block', triggerVariant === 'field' && 'w-full', className)}>
      {label && <label className="mb-1 block text-sm font-medium text-ink-muted">{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          triggerVariant === 'field'
            ? 'flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50'
            : 'flex items-center gap-1.5 rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm font-medium text-ink-muted hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50',
          triggerVariant === 'field' && (error ? 'border-danger-500' : 'border-line-strong'),
        )}
      >
        <span className={cn('truncate', triggerVariant === 'field' && !selected && 'text-ink-subtle')}>
          {renderTrigger ? renderTrigger(selected) : (selected?.label ?? placeholder ?? t('ui.select'))}
        </span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-ink-subtle transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          className={cn(
            'absolute z-50 mt-1 min-w-[10rem] max-w-xs overflow-hidden rounded-md border bg-white shadow-raised',
            align === 'right' ? 'right-0' : 'left-0',
            panelClassName,
          )}
        >
          {showSearch && (
            <div className="flex items-center gap-2 border-b px-2.5 py-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('ui.typeToSearch')}
                className="w-full bg-transparent text-sm outline-none placeholder:text-ink-subtle"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto py-1">
            {loading && <div className="px-3 py-2 text-sm text-ink-subtle">{t('common.loading')}</div>}
            {!loading && filtered.length === 0 && <div className="px-3 py-2 text-sm text-ink-subtle">{t('ui.noResults')}</div>}
            {!loading && filtered.map((option, index) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                disabled={option.disabled}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => select(option)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-surface-hover',
                  index === activeIndex && 'bg-surface-hover',
                  option.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <span className="flex flex-col">
                  <span className={cn(option.value === value && 'font-medium text-brand-700')}>{option.label}</span>
                  {option.description && <span className="text-xs text-ink-subtle">{option.description}</span>}
                </span>
                {option.value === value && <Check className="h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
    </div>
  );
}

// ─── MultiSelect (string[] value, checkbox list) ────────────────────────────

interface MultiSelectProps extends BaseProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  searchable,
  searchThreshold = 8,
  align = 'left',
  className,
  panelClassName,
  label,
  error,
  'aria-label': ariaLabel,
}: MultiSelectProps) {
  const { t } = useI18n();
  const { open, setOpen, query, setQuery, activeIndex, setActiveIndex, containerRef, inputRef, typeAheadRef } = useComboboxOpenState();
  const filtered = filterOptions(options, query);
  const showSearch = searchable ?? options.length > searchThreshold;
  useTypeAheadJump(open, showSearch, options, setActiveIndex, typeAheadRef);

  React.useEffect(() => {
    if (open && showSearch) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, showSearch, inputRef]);

  function toggle(optionValue: string) {
    onChange(value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue]);
  }

  function selectFromKeyboard() {
    const option = filtered[activeIndex];
    if (!option || option.disabled || value.includes(option.value)) return;
    onChange([...value, option.value]);
    setQuery('');
    setActiveIndex(0);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(Math.min(activeIndex + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(Math.max(activeIndex - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      selectFromKeyboard();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  }

  const selectedOptions = value.map((selectedValue) => (
    options.find((option) => option.value === selectedValue) ?? {
      value: selectedValue,
      label: selectedValue,
    }
  ));

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && <label className="mb-1 block text-sm font-medium text-ink-muted">{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex min-h-10 w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50',
          error ? 'border-danger-500' : 'border-line-strong',
        )}
      >
        <span className={cn(
          'flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-left',
          value.length === 0 && 'text-ink-subtle',
        )}>
          {selectedOptions.length === 0 ? (placeholder ?? t('ui.any')) : selectedOptions.map((option) => (
            <span
              key={option.value}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-surface-hover px-2.5 py-1 text-xs text-ink-muted"
            >
              <span className="truncate">
                {option.label}{option.description ? ` (${option.description})` : ''}
              </span>
              <span
                role="button"
                tabIndex={0}
                aria-label={t('ui.removeItem', { label: option.label })}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(option.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    toggle(option.value);
                  }
                }}
                className="shrink-0 cursor-pointer rounded-full text-ink-subtle hover:text-ink"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </span>
          ))}
        </span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 text-ink-subtle transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className={cn(
            'absolute z-50 mt-1 min-w-[14rem] w-full overflow-hidden rounded-md border bg-white shadow-raised',
            align === 'right' ? 'right-0' : 'left-0',
            panelClassName,
          )}
        >
          {showSearch && (
            <div className="flex items-center gap-2 border-b px-2.5 py-2">
              <Search className="h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('ui.typeToSearch')}
                className="w-full bg-transparent text-sm outline-none placeholder:text-ink-subtle"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto py-1">
            {loading && <div className="px-3 py-2 text-sm text-ink-subtle">{t('common.loading')}</div>}
            {!loading && filtered.length === 0 && <div className="px-3 py-2 text-sm text-ink-subtle">{t('ui.noResults')}</div>}
            {!loading && filtered.map((option, index) => {
              const checked = value.includes(option.value);
              return (
                <label
                  key={option.value}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-hover',
                    index === activeIndex && 'bg-surface-hover',
                    option.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={option.disabled}
                    onChange={() => toggle(option.value)}
                    className="h-3.5 w-3.5 rounded border-line-strong"
                  />
                  <span className={cn(checked && 'font-medium text-brand-700')}>{option.label}</span>
                </label>
              );
            })}
          </div>
          {value.length > 0 && (
            <div className="border-t px-3 py-1.5">
              <button type="button" onClick={() => onChange([])} className="text-xs text-ink-subtle hover:text-ink-muted">
                {t('ui.clearSelection')}
              </button>
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
    </div>
  );
}
